/**
 * @fileoverview Servicio de tracking analítico para el flujo de login.
 * Centraliza todos los eventos GTM (Universal Analytics + GA4) y Amplitude
 * relacionados al proceso de autenticación, evitando que esa lógica
 * quede dispersa en el componente Login.vue.
 * @module services/login-analytics.service
 */

import { getHashIS } from '../interfaces/sha256'
import DocumentRules from '../interfaces/documentRules'

/**
 * Construye el hash de identificación de usuario para GTM/Braze.
 * @param {string} numeroDocumento
 * @returns {string}
 */
function buildHashId (numeroDocumento) {
  const tipoDocumento = DocumentRules.getDocumentTypeZS(numeroDocumento)
  return getHashIS(tipoDocumento.toString().padStart(2, '0'), numeroDocumento)
}

/**
 * Registra el evento de intento de login (click en "Ingresar").
 * @param {Object} gtm - Instancia de $gtm (vue-gtm)
 * @param {string} numeroDocumento
 */
export function trackLoginAttempt (gtm, numeroDocumento) {
  const hashId = buildHashId(numeroDocumento)

  gtm.trackEvent({
    userProperties: {
      userID: hashId,
      origen: 'Front',
      numeroDoc: numeroDocumento,
      cliente: true
    }
  })

  gtm.trackEvent({
    event: 'virtualEvent',
    category: 'Zona Segura',
    action: 'Inicio de sesión',
    label: 'Login'
  })

  gtm.trackEvent({
    event: 'select_button',
    'Nombre Sección': 'Zona Segura',
    TipoElemento: 'Botón',
    TipoAccion: 'Inicio de sesión',
    CTA: 'Login'
  })
}

/**
 * Registra el evento de login exitoso en GTM y Amplitude.
 * @param {Object} gtm - Instancia de $gtm
 * @param {Object} amplitude - Instancia de $amplitude
 * @param {Object} responseData - Datos de la respuesta del backend
 * @param {string} numeroDocumento
 * @param {number} tipoDocumento
 * @param {boolean} isFirstLogin - Si es el primer login del usuario
 * @returns {string} Nombre del evento Amplitude disparado
 */
export function trackLoginSuccess (gtm, amplitude, responseData, numeroDocumento, tipoDocumento, isFirstLogin) {
  const hashId = buildHashId(numeroDocumento)

  gtm.trackEvent({
    userID: hashId,
    loginStatus: 'Sesión Iniciada',
    empresa: 'Interseguro'
  })

  let identify = new amplitude.Identify()
    .set('Numero Documento', numeroDocumento)
    .set('Tipo Documento', DocumentRules.getDocumentTypeBUC(tipoDocumento))
    .set('Edad', responseData.edad || '')

  const edad = responseData.edad
  let range = ''
  if (edad >= 18 && edad <= 24) range = '18-24'
  else if (edad >= 25 && edad <= 34) range = '25-34'
  else if (edad >= 35 && edad <= 44) range = '35-44'
  else if (edad >= 45 && edad <= 54) range = '45-54'
  else if (edad >= 55 && edad <= 64) range = '55-64'
  else if (edad >= 65) range = '65+'
  else range = '18-'

  identify = identify.set('Edad Promedio', range)
  amplitude.getInstance().setUserId(numeroDocumento.trim())
  amplitude.getInstance().identify(identify)

  const eventProps = { Edad: responseData.edad, DNI: numeroDocumento }
  let eventName = 'Usuario Loggeado'

  if (isFirstLogin) {
    gtm.trackEvent({
      event: 'virtualEvent',
      category: 'Zona Segura',
      action: 'Primer inicio de sesión',
      label: 'Ingreso satisfactorio'
    })
    gtm.trackEvent({
      event: 'select_button',
      'Nombre Sección': 'Zona Segura',
      TipoElemento: 'Botón',
      TipoAccion: ' Primer inicio de sesión ',
      CTA: 'Ingreso satisfactorio'
    })
    eventName = 'First Login'
    amplitude.getInstance().logEvent('SignupOK', { DNI: numeroDocumento })
  }

  amplitude.getInstance().logEvent(eventName, eventProps)
  return eventName
}

/**
 * Registra un evento de error de login.
 * @param {Object} gtm - Instancia de $gtm
 * @param {string} label - Etiqueta descriptiva del error
 */
export function trackLoginError (gtm, label) {
  gtm.trackEvent({
    event: 'virtualEvent',
    category: 'Zona Segura',
    action: 'Inicio de sesión',
    label
  })
  gtm.trackEvent({
    event: 'select_button',
    'Nombre Sección': 'Zona Segura',
    TipoElemento: 'Botón',
    TipoAccion: 'Errores en Inicio de Sesión',
    CTA: label
  })
}

/**
 * Registra el evento de carga de la pantalla de login (mounted).
 * @param {Object} gtm - Instancia de $gtm
 * @param {Object} amplitude - Instancia de $amplitude
 */
export function trackLoginPageView (gtm, amplitude) {
  amplitude.getInstance().identify(new amplitude.Identify())
  amplitude.getInstance().logEvent('Landing Iniciar Sesión', { 'Landing Inicio de Sesión': 1 })

  gtm.trackEvent({
    userID: '(not available)',
    loginStatus: 'Sesión no Iniciada',
    empresa: 'Interseguro'
  })
}



// WEBPACK FOOTER //
// ./src/services/login-analytics.service.js